/**
 * Tax Rates — manage page.
 *
 * DataTable + Sheet CRUD over the shared AppSettings tax rates. The form
 * converts the percent string to/from `ratePercentE4` BigInt.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle, Pencil, Percent as PercentIcon, Plus, Trash2, Undo2 } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import {
  TaxRateForm,
  type TaxRateFormValues,
  e4ToPercent,
  type TaxType,
} from "../../../components/forms/TaxRateForm";
import { useAppSettings, type SharedTaxRateRow } from "../../../lib/appSettings";

export const Route = createFileRoute("/_app/settings/tax-rates")({
  component: TaxRatesPage,
});

type TaxRateRow = SharedTaxRateRow;

const TAX_TYPE_LABEL: Record<string, string> = {
  vat: "VAT",
  duty: "Duty",
  withholding: "Withholding",
  other: "Other",
};

function TaxRatesPage() {
  const { taxRates: rows, updateTaxRates: setRows } = useAppSettings();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<TaxRateRow | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<TaxRateRow | null>(null);

  function openCreate() {
    setEditing(null);
    setSheetOpen(true);
  }

  function openEdit(row: TaxRateRow) {
    setEditing(row);
    setSheetOpen(true);
  }

  function handleSubmit(values: TaxRateFormValues) {
    if (editing) {
      setRows((prev) =>
        prev.map((r) =>
          r._id === editing._id
            ? {
                ...r,
                displayName: values.displayName,
                taxType: values.taxType,
                ratePercentE4: values.ratePercentE4,
                jurisdiction: values.jurisdiction,
                isActive: values.isActive,
              }
            : r,
        ),
      );
      toast.success("Tax rate updated. Audit trail recorded.");
    } else {
      const newId = `tax_${Date.now()}`;
      setRows((prev) => [
        ...prev,
        {
          _id: newId,
          code: values.code,
          displayName: values.displayName,
          taxType: values.taxType,
          ratePercentE4: values.ratePercentE4,
          jurisdiction: values.jurisdiction,
          isActive: values.isActive,
          isDefault: false,
          deletedAt: null,
        },
      ]);
      toast.success("Tax rate created (mock)");
    }
    setSheetOpen(false);
  }

  function handleArchive(row: TaxRateRow) {
    setArchiveTarget(row);
  }

  function confirmArchive() {
    if (!archiveTarget) return;
    setRows((prev) =>
      prev.map((r) =>
        r._id === archiveTarget._id
          ? { ...r, isActive: false, deletedAt: Date.now() }
          : r,
      ),
    );
    toast.success("Tax rate archived");
    setArchiveTarget(null);
  }

  function handleRestore(row: TaxRateRow) {
    setRows((prev) =>
      prev.map((r) =>
        r._id === row._id ? { ...r, isActive: true, deletedAt: null } : r,
      ),
    );
    toast.success("Tax rate restored");
  }

  const columns = useMemo<DataTableColumn<TaxRateRow>[]>(
    () => [
      {
        id: "code",
        accessorKey: "code",
        header: "Code",
        cell: (info) => (
          <span className="font-mono text-(--text-primary)">
            {String(info.getValue())}
          </span>
        ),
      },
      {
        id: "displayName",
        accessorKey: "displayName",
        header: "Name",
        cell: (info) => (
          <span className="text-(--text-primary)">
            {info.row.original.displayName}
          </span>
        ),
      },
      {
        id: "taxType",
        header: "Type",
        cell: (info) => (
          <Badge variant="neutral" size="sm">
            {TAX_TYPE_LABEL[info.row.original.taxType] ??
              info.row.original.taxType}
          </Badge>
        ),
      },
      {
        id: "rate",
        header: "Rate",
        cell: (info) => (
          <span className="tabular-nums text-(--text-primary)">
            {e4ToPercent(info.row.original.ratePercentE4)}%
          </span>
        ),
      },
      {
        id: "jurisdiction",
        header: "Jurisdiction",
        cell: (info) => (
          <span className="text-(--text-secondary)">
            {info.row.original.jurisdiction || "—"}
          </span>
        ),
      },
      {
        id: "isActive",
        header: "Status",
        cell: (info) => {
          const r = info.row.original;
          if (r.deletedAt !== null) {
            return (
              <Badge variant="neutral" size="sm">
                Archived
              </Badge>
            );
          }
          return (
            <Badge variant={r.isActive ? "success" : "neutral"} size="sm">
              {r.isActive ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(r);
                }}
              >
                <Pencil className="size-3.5" />
              </Button>
              {r.deletedAt !== null ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(r);
                  }}
                >
                  <Undo2 className="size-3.5" />
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchive(r);
                  }}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Tax Rates"
        description="VAT, duty and withholding rates referenced by invoices and POs. Archive instead of deleting — historical invoices keep their original rate."
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" />
            Add tax rate
          </Button>
        }
      />

      <DataTable
        data={rows}
        columns={columns}
        emptyTitle="No tax rates yet"
        emptyDescription="Create your first tax rate to start invoicing."
        emptyIcon={<PercentIcon className="size-5 text-(--text-tertiary)" />}
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>
              {editing ? "Edit tax rate" : "New tax rate"}
            </SheetTitle>
            <SheetDescription>
              {editing
                ? "Update the rate or label. Code is locked once created."
                : "Use percent — the system stores it as basis-points internally so rounding is exact."}
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <TaxRateForm
              key={editing?._id ?? "new"}
              isEdit={Boolean(editing)}
              initialValues={
                editing
                  ? {
                      code: editing.code,
                      displayName: editing.displayName,
                      taxType: editing.taxType as TaxType,
                      ratePercentE4: editing.ratePercentE4,
                      jurisdiction: editing.jurisdiction,
                      isActive: editing.isActive,
                    }
                  : undefined
              }
              onSubmit={handleSubmit}
              onCancel={() => setSheetOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Dialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-amber-500" />
              Archive tax rate
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col gap-3 text-sm">
                <p>
                  Archiving{" "}
                  <strong>{archiveTarget?.displayName}</strong>{" "}
                  ({archiveTarget?.code}) will make it unavailable for new
                  invoices and purchase orders.
                </p>
                <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                  <span>
                    Warning: Historical records already using this rate will NOT
                    be affected — they keep their original rate. However, users
                    won't be able to select this rate for new transactions.
                  </span>
                </p>
                <p>Are you sure?</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setArchiveTarget(null)}
            >
              Cancel
            </Button>
            <Button variant="secondary" onClick={confirmArchive}>
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
